/* eslint-disable @typescript-eslint/no-explicit-any */
import PropTypes from 'prop-types';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import SimpleBar from 'simplebar-react';
import { withTranslation } from 'react-i18next';
import { withRouter, Link } from 'react-router-dom';
import { InnoboIcon } from '../../components/Common/Icon/InnoboIcon';
import { changeTitleBanner } from '../../store/actions';
import { useDispatch } from 'react-redux';
import FooterCopyright from './FooterCopyright';
import './sidebar-menu.scss';
import './sidebar-search.scss';

// Por qué este menú se arma así:
// - Cada ruta trae su propio <Layout> (ver Authmiddleware), así que el menú se vuelve a montar
//   en cada navegación. Por eso el estado no puede vivir solo en memoria: la pantalla activa sale
//   de la URL y las carpetas abiertas y el scroll se guardan en sessionStorage.
// - Solo una carpeta abierta por nivel (acordeón): abrir una cierra las demás ramas.
// - Ya no se usa MetisMenu: le ponía y quitaba clases al DOM por su cuenta y peleaba con React.
// - Buscador: filtra por nombre (sin tildes ni mayúsculas), abre las carpetas de lo que coincide y
//   Enter abre la primera pantalla. "/" lo enfoca desde cualquier parte, Esc lo limpia.
const OPEN_KEY = 'menuOpenIds';
const SCROLL_KEY = 'menuScrollTop';

const leerJSON = (valor: string | null | undefined, porDefecto: any) => {
    try {
        return valor ? JSON.parse(valor) : porDefecto;
    } catch {
        return porDefecto;
    }
};

// Los dashboards de BI van a /Analytics/:id: la URL nunca nombra al proveedor (ver allRoutes).
const prepararItems = (raw: any) =>
    (Array.isArray(raw) ? raw : []).map((item: any) => {
        const copia = { ...item };
        if (copia.identificator === 'Holistics') copia.url_menu = '/Analytics/' + copia.id;
        if (copia.identificator === 'iFrame') copia.url_menu = '/Iframe/' + copia.id;
        return copia;
    });

const esCarpeta = (item: any) => item.url_menu === '#';
const sidebarChico = () => document.body.getAttribute('data-sidebar-size') === 'sm';
const normalizar = (texto: any) =>
    String(texto || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim();
const escribiendo = (el: any) =>
    !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable);

// La pantalla activa es la opción cuya URL coincide con la ruta (o es su prefijo más largo,
// para que /Shipments/123 deje marcada la opción /Shipments).
const buscarActivo = (items: any[], pathname: string) => {
    const ruta = (pathname || '').toLowerCase().replace(/\/+$/, '');
    let mejor: any = null;
    items.forEach((item: any) => {
        if (!item.url_menu || esCarpeta(item)) return;
        const url = String(item.url_menu).toLowerCase().replace(/\/+$/, '');
        if (!url) return;
        if (ruta === url || ruta.startsWith(url + '/')) {
            if (!mejor || url.length > mejor.url.length) mejor = { item, url };
        }
    });
    return mejor ? mejor.item : null;
};

// Ids de las carpetas que contienen al item, de la de arriba hacia abajo.
const ancestros = (porId: Map<any, any>, item: any) => {
    const ids: any[] = [];
    let actual = item ? porId.get(item.padreId) : undefined;
    let guardia = 0;
    while (actual && guardia++ < 10) {
        ids.unshift(actual.id);
        actual = porId.get(actual.padreId);
    }
    return ids;
};

const SidebarContent = (props: any) => {
    const ref = useRef<any>();
    const inputRef = useRef<HTMLInputElement>(null);
    const dispatch = useDispatch();

    const [items] = useState<any[]>(() =>
        prepararItems(leerJSON(props.d || localStorage.getItem('permisos'), []))
    );
    const porId = useMemo(() => new Map<any, any>(items.map((i: any) => [i.id, i] as [any, any])), [items]);
    const hijosDe = useMemo(() => {
        const mapa = new Map<any, any[]>();
        items.forEach((i: any) => {
            if (i.padreId === null || i.padreId === undefined) return;
            if (!mapa.has(i.padreId)) mapa.set(i.padreId, []);
            (mapa.get(i.padreId) as any[]).push(i);
        });
        return mapa;
    }, [items]);
    const raiz = useMemo(() => items.filter((i: any) => i.padreId === null || i.padreId === undefined), [items]);

    const pathname = props.location?.pathname || '';
    const activo = useMemo(() => buscarActivo(items, pathname), [items, pathname]);
    const rutaActiva = useMemo(() => (activo ? ancestros(porId, activo) : []), [porId, activo]);

    // Carpetas abiertas = la rama de la pantalla actual. Si la ruta no está en el menú,
    // se respeta lo que el usuario tenía abierto.
    const ramaDe = (item: any) => {
        const rama = ancestros(porId, item);
        return hijosDe.has(item.id) ? [...rama, item.id] : rama;
    };
    const [abiertos, setAbiertos] = useState<any[]>(() =>
        activo ? ramaDe(activo) : leerJSON(sessionStorage.getItem(OPEN_KEY), [])
    );

    // Buscador. Con el menú colapsado no se ve, así que tampoco filtra.
    const [texto, setTexto] = useState('');
    const consulta = sidebarChico() ? '' : normalizar(texto);
    const busqueda = useMemo(() => {
        if (!consulta) return null;
        const visibles = new Set<any>();
        const coincide = new Set<any>();
        const agregarDescendientes = (id: any, prof: number) => {
            if (prof > 10) return;
            (hijosDe.get(id) || []).forEach((h: any) => {
                visibles.add(h.id);
                agregarDescendientes(h.id, prof + 1);
            });
        };
        items.forEach((i: any) => {
            if (!normalizar(i.nombre).includes(consulta)) return;
            coincide.add(i.id);
            visibles.add(i.id);
            ancestros(porId, i).forEach((id) => visibles.add(id));
            if (hijosDe.has(i.id)) agregarDescendientes(i.id, 0);
        });
        // Primera pantalla en el orden del menú: la que coincide o, si solo coincidió una carpeta, su primera hoja.
        let primera: any = null;
        let primeraVisible: any = null;
        const recorrer = (lista: any[], prof: number) => {
            for (const i of lista) {
                if (primera || prof > 10) return;
                if (!visibles.has(i.id)) continue;
                if (!esCarpeta(i)) {
                    if (coincide.has(i.id)) primera = i;
                    else if (!primeraVisible) primeraVisible = i;
                }
                recorrer(hijosDe.get(i.id) || [], prof + 1);
            }
        };
        recorrer(raiz, 0);
        return { visibles, coincide, primera: primera || primeraVisible };
    }, [consulta, items, porId, hijosDe, raiz]);

    // Misma ruta montada con otro parámetro (ej. /Iframe/1 -> /Iframe/2) no vuelve a montar el menú.
    useEffect(() => {
        if (!activo) return;
        const rama = ancestros(porId, activo);
        setAbiertos(hijosDe.has(activo.id) ? [...rama, activo.id] : rama);
    }, [activo, porId, hijosDe]);

    useEffect(() => {
        sessionStorage.setItem(OPEN_KEY, JSON.stringify(abiertos));
    }, [abiertos]);

    useEffect(() => {
        ref.current?.recalculate?.();
    });

    // Scroll: se recupera donde estaba y, si la opción activa quedó fuera de la vista, se centra.
    useLayoutEffect(() => {
        const scroller = ref.current?.getScrollElement?.();
        if (!scroller) return;
        const guardado = Number(sessionStorage.getItem(SCROLL_KEY));
        if (guardado) scroller.scrollTop = guardado;
        const el = scroller.querySelector('a.activeChild');
        if (el) {
            const r = el.getBoundingClientRect();
            const s = scroller.getBoundingClientRect();
            if (r.top < s.top || r.bottom > s.bottom) {
                scroller.scrollTop += r.top - s.top - s.height / 2 + r.height / 2;
            }
        }
        const alScroll = () => sessionStorage.setItem(SCROLL_KEY, String(scroller.scrollTop));
        scroller.addEventListener('scroll', alScroll, { passive: true } as any);
        return () => scroller.removeEventListener('scroll', alScroll);
    }, []);

    // Al buscar, los resultados quedan arriba.
    useEffect(() => {
        if (!consulta) return;
        const scroller = ref.current?.getScrollElement?.();
        if (scroller) scroller.scrollTop = 0;
    }, [consulta]);

    // "/" enfoca el buscador desde cualquier parte (menos cuando se está escribiendo en un campo).
    useEffect(() => {
        const alTeclear = (e: KeyboardEvent) => {
            if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey) return;
            if (escribiendo(document.activeElement) || sidebarChico()) return;
            e.preventDefault();
            inputRef.current?.focus();
        };
        document.addEventListener('keydown', alTeclear);
        return () => document.removeEventListener('keydown', alTeclear);
    }, []);

    const estaAbierto = (item: any) =>
        abiertos.includes(item.id) ||
        // Menú colapsado: la carpeta de arriba muestra todo su árbol en el flotante, como antes.
        (sidebarChico() && ancestros(porId, item).some((id) => abiertos.includes(id)));

    // Abrir una carpeta deja abierta solo su rama; volver a hacer click la cierra.
    const alternarCarpeta = (item: any) => {
        const rama = ancestros(porId, item);
        setAbiertos((prev) => (prev.includes(item.id) ? rama : [...rama, item.id]));
    };

    const abrirPantalla = (item: any) => {
        if (!item || esCarpeta(item)) return;
        if (item.url_menu === '/home') dispatch(changeTitleBanner(''));
        setTexto('');
        props.history?.push(item.url_menu);
    };

    // Resalta el pedazo del nombre que coincide (los índices sirven aunque haya tildes).
    const conResaltado = (nombre: string, coincide: boolean) => {
        if (!consulta || !coincide) return nombre;
        const idx = normalizar(nombre).indexOf(consulta);
        if (idx < 0 || idx + consulta.length > nombre.length) return nombre;
        return (
            <>
                {nombre.substring(0, idx)}
                <b className="mn-hit">{nombre.substring(idx, idx + consulta.length)}</b>
                {nombre.substring(idx + consulta.length)}
            </>
        );
    };

    const renderItems = (lista: any[], nivel: number): any =>
        lista.map((item: any) => {
            if (busqueda && !busqueda.visibles.has(item.id)) return null;
            const hijos = hijosDe.get(item.id) || [];
            const tieneHijos = hijos.length > 0;
            const carpeta = esCarpeta(item);
            if (carpeta && nivel > 0 && !tieneHijos) return null;

            const iframeConHijos = item.identificator === 'iFrame' && tieneHijos;
            const desplegable = carpeta || iframeConHijos;
            // Buscando, todo lo visible va abierto para que se vea dónde está cada resultado.
            const abierto = desplegable && tieneHijos && (busqueda ? true : estaAbierto(item));
            const esActivo = !!activo && activo.id === item.id;
            const contieneActivo = rutaActiva.includes(item.id);
            const esPrimera = !!busqueda && busqueda.primera?.id === item.id;
            const nombre: string = item?.nombre?.length > 26 ? item.nombre.substring(0, 26) : item?.nombre || '';

            const clase = desplegable
                ? `has-arrow ${contieneActivo ? 'activeParent' : ''} ${iframeConHijos && esActivo ? 'activeChild' : ''}`
                : `d-flex ${esActivo ? 'activeChild' : ''} ${esPrimera ? 'mn-first' : ''} ${nombre.length >= 19 ? 'OverSizeTextMenu' : ''} text-nowrap`;

            return (
                <li key={item.id} className={abierto ? 'mm-active' : ''}>
                    <Link
                        title={item.nombre}
                        id={item.id}
                        to={{ pathname: item.url_menu }}
                        className={clase}
                        aria-current={esActivo ? 'page' : undefined}
                        onClick={(e: any) => {
                            if (carpeta) {
                                e.preventDefault();
                                if (busqueda) return;
                                if (sidebarChico() && nivel > 0) return;
                                alternarCarpeta(item);
                                return;
                            }
                            if (iframeConHijos && !busqueda) alternarCarpeta(item);
                            if (busqueda) setTexto('');
                            if (item.url_menu === '/home') dispatch(changeTitleBanner(''));
                        }}
                    >
                        <InnoboIcon name={item.icon} />
                        <span>{conResaltado(nombre, !!busqueda && busqueda.coincide.has(item.id))}</span>
                    </Link>
                    {abierto && <ul className="sub-menu">{renderItems(hijos, nivel + 1)}</ul>}
                </li>
            );
        });

    return (
        <React.Fragment>
            <SimpleBar style={{ maxHeight: '100%' }} ref={ref}>
                <div id="sidebar-menu">
                    <ul className="metismenu list-unstyled" id="side-menu">
                        <li className="menu-title"> {props.t('Menu')} </li>
                        <li className="mn-search">
                            <div className="mn-search__box">
                                <i className="mdi mdi-magnify" />
                                <input
                                    ref={inputRef}
                                    type="search"
                                    value={texto}
                                    placeholder={props.t('Search menu')}
                                    aria-label={props.t('Search menu')}
                                    autoComplete="off"
                                    spellCheck={false}
                                    onChange={(e) => setTexto(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            abrirPantalla(busqueda?.primera);
                                        }
                                        if (e.key === 'Escape') {
                                            setTexto('');
                                            inputRef.current?.blur();
                                        }
                                    }}
                                />
                                {texto ? (
                                    <button
                                        type="button"
                                        className="mn-search__clear"
                                        aria-label={props.t('Clear')}
                                        onClick={() => {
                                            setTexto('');
                                            inputRef.current?.focus();
                                        }}
                                    >
                                        <i className="mdi mdi-close" />
                                    </button>
                                ) : (
                                    <kbd className="mn-search__kbd">/</kbd>
                                )}
                            </div>
                        </li>
                        {renderItems(raiz, 0)}
                        {busqueda && busqueda.visibles.size === 0 ? (
                            <li className="mn-empty">{props.t('No results')}</li>
                        ) : null}
                    </ul>
                </div>
            </SimpleBar>
            <div className="containerFooterSidebar">
                <FooterCopyright />
            </div>
        </React.Fragment>
    );
};

SidebarContent.propTypes = {
    location: PropTypes.object,
    history: PropTypes.object,
    t: PropTypes.any,
};

export default withTranslation()(withRouter(SidebarContent));
